import { useEffect, useState, useMemo, useCallback, useRef, lazy, Suspense } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAppDispatch, useAppSelector, useAuth, useFieldAvailability } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts";
import { Button, StepsSwitcher, Toast, Prompt, PromptForm } from "@/shared/ui";
import { HR_CONTRACTS_PAGE_ROUTE, formatDateToISO } from "@/shared/utils";
import { useHiringGetApplicationDetailQuery } from "@/features/hr/hiring/api";
import type { ContractFormValues, SectionId, CreateContractDto } from "@/features/hr/contracts/types";
import {
  useCreateContractMutation,
  useGetNextContractNumberQuery,
  useGetContractFillDraftQuery,
  useUpsertContractFillDraftMutation,
  useGetContractDetailQuery,
  usePatchContractMutation,
} from "@/features/hr/contracts/api";
import {
  extractErrorMessage,
  ContractSchema,
  isSectionComplete,
  draftResponseToFormValues,
  formValuesToDraftPayload,
  contractDetailToFormValues,
  formValuesToContractPatch,
} from "@/features/hr/contracts/utils";
import { DEFAULT_CONTRACT_VALUES, SECTION_FIELDS, SECTIONS } from "@/features/hr/contracts/consts";
import { setContractData, setSectionCompleteness, setReadyForSigning, resetContractState } from "@/features/hr/contracts/slice";
import { useLeaveProtection } from "@/features/hr/contracts/hooks";
import { FillContractHeader, FillContractPageSkeleton, SectionRenderer } from "./components";

const ContractPreviewModal = lazy(() =>
  import("./components/ContractPreviewModal").then((m) => ({ default: m.default })),
);

export default function FillContractPage() {
  const { applicationId: applicationIdParam, contractId: contractIdParam } = useParams<{
    applicationId: string;
    contractId: string;
  }>();
  const { t } = useTranslation("FillContractPage");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { hasPermission } = useAuth();
  const savedData = useAppSelector((state) => state.workContract.data) as ContractFormValues | undefined;
  const completeness = useAppSelector((state) => state.workContract.sectionCompleteness);

  const isEditMode = !!contractIdParam;
  const contractId = contractIdParam ? Number(contractIdParam) : undefined;
  const applicationId = applicationIdParam ? Number(applicationIdParam) : undefined;

  const {
    data: applicationDetail,
    isLoading: isLoadingApplication,
    isError: isErrorApplication,
  } = useHiringGetApplicationDetailQuery(applicationId!, {
    skip: !applicationId || isEditMode,
  });

  const { data: fillDraft, isLoading: isFillDraftLoading } = useGetContractFillDraftQuery(applicationId!, {
    skip: !applicationId || isEditMode,
  });

  const {
    data: contractDetail,
    isLoading: isLoadingContract,
    isError: isErrorContract,
  } = useGetContractDetailQuery(contractId!, {
    skip: !contractId,
    refetchOnMountOrArgChange: true,
  });

  const { data: editApplicationDetail } = useHiringGetApplicationDetailQuery(contractDetail?.candidate_application?.id!, {
    skip: !isEditMode || !contractDetail?.candidate_application?.id,
  });

  const { data: nextNumberData } = useGetNextContractNumberQuery(undefined, { skip: isEditMode });

  const canSaveDraft = hasPermission(PERMISSIONS.HR.CREATE_CONTRACT);
  const [upsertFillDraft] = useUpsertContractFillDraftMutation();
  const [patchContract] = usePatchContractMutation();
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftSaveStatus, setDraftSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const draftRestoredRef = useRef(false);
  const contractRestoredRef = useRef(false);
  const lastSavedContractValuesRef = useRef<string | null>(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const contractNumberHintRef = useRef<string | null>(null);

  useEffect(function resetReduxOnEditMount() {
    if (!isEditMode) return;
    dispatch(resetContractState());
  }, []);

  const defaultValues = useMemo<ContractFormValues>(() => {
    if (isEditMode) return DEFAULT_CONTRACT_VALUES;
    const today = formatDateToISO(new Date());
    const base = savedData ?? DEFAULT_CONTRACT_VALUES;
    const withDefaultDate = {
      ...base,
      sign_date: base.sign_date || today,
      start_date: base.start_date || today,
    };
    if (applicationDetail) {
      return {
        ...withDefaultDate,
        employee_address_ru: withDefaultDate.employee_address_ru || applicationDetail.addresses?.registration || "",
        job_position_ru: withDefaultDate.job_position_ru || applicationDetail.job_position || "",
      };
    }
    return withDefaultDate;
  }, [savedData, applicationDetail, isEditMode]);

  const methods = useForm<ContractFormValues>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(ContractSchema),
    defaultValues,
  });

  useEffect(() => {
    if (isEditMode || !applicationDetail) return;
    if (!methods.getValues("employee_address_ru")) {
      methods.setValue("employee_address_ru", applicationDetail.addresses?.registration || "", { shouldDirty: false });
    }
    if (!methods.getValues("job_position_ru")) {
      methods.setValue("job_position_ru", applicationDetail.job_position || "", { shouldDirty: false });
    }
  }, [applicationDetail, methods, isEditMode]);

  useEffect(function restoreFillDraft() {
    if (isEditMode) return;
    if (draftRestoredRef.current || isFillDraftLoading) return;
    draftRestoredRef.current = true;
    if (fillDraft) {
      const today = formatDateToISO(new Date());
      const restored = draftResponseToFormValues(fillDraft);
      methods.reset({
        ...DEFAULT_CONTRACT_VALUES,
        ...restored,
        sign_date: restored.sign_date || today,
        start_date: restored.start_date || today,
      });
    }
    if (fillDraft?.last_active_section) {
      setActive(fillDraft.last_active_section as SectionId);
    }
    setDraftRestored(true);
  }, [fillDraft, isFillDraftLoading, isEditMode]);

  useEffect(function restoreContractDetail() {
    if (!isEditMode) return;
    if (contractRestoredRef.current || isLoadingContract || !contractDetail) return;
    contractRestoredRef.current = true;
    const restored = { ...DEFAULT_CONTRACT_VALUES, ...contractDetailToFormValues(contractDetail) };
    methods.reset(restored);

    for (const section of SECTIONS) {
      dispatch(setSectionCompleteness({
        section: section.id,
        complete: isSectionComplete(section.id, restored as ContractFormValues, {}),
      }));
    }
    setDraftRestored(true);
  }, [contractDetail, isLoadingContract, isEditMode]);

  useEffect(function prefillContractNumber() {
    if (isEditMode || !draftRestored || !nextNumberData) return;
    if (methods.getValues("contract_number")) return;
    methods.setValue("contract_number", String(nextNumberData.next), { shouldDirty: false });
    contractNumberHintRef.current = String(nextNumberData.next);
  }, [isEditMode, draftRestored, nextNumberData, methods]);

  const { modalOpen, confirmNavigation, cancelNavigation, requestConfirmation } = useLeaveProtection(methods);

  const [active, setActive] = useState<SectionId>("basic_info");
  const [sectionsWithErrors, setSectionsWithErrors] = useState<SectionId[]>([]);
  const [showGlobalError, setShowGlobalError] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [showSuccessPrompt, setShowSuccessPrompt] = useState(false);
  const [errorPrompt, setErrorPrompt] = useState<{ title: string; text: string } | null>(null);
  const [previewData, setPreviewData] = useState<CreateContractDto | null>(null);
  const [isUpdatingContract, setIsUpdatingContract] = useState(false);

  const [createContract, { isLoading: isCreatingContract }] = useCreateContractMutation();
  const [createdNumber, setCreatedNumber] = useState<string | null>(null);

  const contractNumberValue = methods.watch("contract_number") ?? "";
  const contractNumberAvailability = useFieldAvailability({
    resource: "contract",
    field: "contract_number",
    value: contractNumberValue,
    isFormatValid: /^\d{1,18}$/.test(contractNumberValue) && Number(contractNumberValue) > 0,
    exclude_id: isEditMode ? contractId : undefined,
  });
  const isNumberTaken = contractNumberAvailability.state === "taken";
  const contractNumberError = isNumberTaken ? t("validation.contract_number_taken") : undefined;
  const isPureDraft =
    !isEditMode ||
    (contractDetail?.signing_status === 0 &&
      !contractDetail?.signing_document &&
      !contractDetail?.worker &&
      !contractDetail?.is_legacy_import);

  useEffect(() => {
    const subscription = methods.watch((_, { name }) => {
      if (!name) return;

      const found = Object.entries(SECTION_FIELDS).find(([_, fields]) => {
        if (name.includes(".")) {
          const fieldName = name.split(".")[0];
          return fields.includes(fieldName);
        }
        return fields.includes(name as keyof ContractFormValues);
      });
      if (!found) return;

      const section = found[0] as SectionId;

      methods.trigger(name).then(() => {
        const values = methods.getValues();
        const errors = methods.formState.errors as Record<string, unknown>;
        const valid = isSectionComplete(section, values, errors);

        dispatch(
          setSectionCompleteness({
            section,
            complete: valid,
          }),
        );
      });
    });

    return () => subscription.unsubscribe();
  }, [methods.watch, dispatch, methods]);

  const sectionIds = SECTIONS.map((s) => s.id);
  const activeIndex = sectionIds.indexOf(active);
  const isFirstSection = activeIndex === 0;
  const isLastSection = activeIndex === sectionIds.length - 1;

  function handleChangeSection(next: string) {
    setActive(next as SectionId);
  }

  function handleNext() {
    if (!isLastSection) {
      setActive(sectionIds[activeIndex + 1]);
    }
  }

  function handleBack() {
    if (!isFirstSection) {
      setActive(sectionIds[activeIndex - 1]);
    }
  }

  const handleSaveFillDraft = useCallback(
    async function handleSaveFillDraft() {
      if (isSavingDraft || !applicationId) return;
      try {
        setIsSavingDraft(true);
        setDraftSaveStatus("saving");
        const formValues = methods.getValues();
        await upsertFillDraft({
          applicationId,
          ...formValuesToDraftPayload(formValues, active),
        }).unwrap();
        setDraftSaveStatus("saved");
      } catch {
        setDraftSaveStatus("error");
      } finally {
        setIsSavingDraft(false);
      }
    },
    [isSavingDraft, applicationId, methods, upsertFillDraft, active],
  );

  const handleSaveContract = useCallback(
    async function handleSaveContract() {
      if (isSavingDraft || !contractId) return;
      try {
        setIsSavingDraft(true);
        setDraftSaveStatus("saving");
        const formValues = methods.getValues();
        const patch = formValuesToContractPatch(formValues);
        if (isNumberTaken) delete patch.contract_number;
        await patchContract({ id: contractId, data: patch }).unwrap();
        lastSavedContractValuesRef.current = JSON.stringify(formValues);
        setDraftSaveStatus("saved");
      } catch {
        setDraftSaveStatus("error");
      } finally {
        setIsSavingDraft(false);
      }
    },
    [isSavingDraft, contractId, methods, patchContract, isNumberTaken],
  );

  const saveFillDraftRef = useRef(handleSaveFillDraft);
  useEffect(function syncSaveFillDraftRef() {
    saveFillDraftRef.current = handleSaveFillDraft;
  }, [handleSaveFillDraft]);

  const saveContractRef = useRef(handleSaveContract);
  useEffect(function syncSaveContractRef() {
    saveContractRef.current = handleSaveContract;
  }, [handleSaveContract]);

  useEffect(
    function autosaveFillDraft() {
      if (isEditMode) return;
      if (!canSaveDraft || !applicationId) return;
      let timer: ReturnType<typeof setTimeout> | null = null;
      const subscription = methods.watch(function onFormChange() {
        if (timer) clearTimeout(timer);
        timer = setTimeout(function fire() {
          if (!draftRestoredRef.current) return;
          if (!methods.formState.isDirty) return;
          void saveFillDraftRef.current();
        }, 800);
      });
      return function cleanup() {
        if (timer) clearTimeout(timer);
        subscription.unsubscribe();
      };
    },
    [canSaveDraft, applicationId, methods, isEditMode],
  );

  useEffect(
    function flushFillOnSectionChange() {
      if (isEditMode) return;
      if (!canSaveDraft || !applicationId) return;
      if (!draftRestoredRef.current) return;
      if (!methods.formState.isDirty) return;
      void saveFillDraftRef.current();
    },
    [active, canSaveDraft, applicationId, methods, isEditMode],
  );

  useEffect(
    function flushContractOnSectionChange() {
      if (!isEditMode) return;
      if (!canSaveDraft || !contractId) return;
      if (!contractRestoredRef.current) return;
      if (!methods.formState.isDirty) return;
      if (JSON.stringify(methods.getValues()) === lastSavedContractValuesRef.current) return;
      void saveContractRef.current();
    },
    [active, canSaveDraft, contractId, methods, isEditMode],
  );

  function buildPreviewData(formValues: ContractFormValues): CreateContractDto {
    if (isEditMode) {
      return {
        application_id: contractDetail?.candidate_application?.id ?? 0,
        sign_date: formValues.sign_date,
        start_date: formValues.start_date,
        work_city_id: formValues.work_city_id!,
        job_position_ru: formValues.job_position_ru,
        job_position_kk: formValues.job_position_kk,
        job_duties_ru: formValues.job_duties_ru,
        job_duties_kk: formValues.job_duties_kk,
        trial_period: formValues.trial_period,
        trial_duration_months: formValues.trial_period ? formValues.trial_duration_months : undefined,
        work_start_time: formValues.work_start_time,
        work_end_time: formValues.work_end_time,
        break_start_time: formValues.has_break ? formValues.break_start_time : undefined,
        break_end_time: formValues.has_break ? formValues.break_end_time : undefined,
        working_days_list: formValues.working_days_list,
        salary_amount: formValues.salary_amount,
        employee_address_ru: formValues.employee_address_ru,
        employee_address_kk: formValues.employee_address_kk,
        work_format: formValues.work_format,
        is_parallel_worker: formValues.is_parallel_worker,
      };
    }
    return {
      application_id: applicationId!,
      sign_date: formValues.sign_date,
      start_date: formValues.start_date,
      work_city_id: formValues.work_city_id!,
      job_position_ru: formValues.job_position_ru,
      job_position_kk: formValues.job_position_kk,
      job_duties_ru: formValues.job_duties_ru,
      job_duties_kk: formValues.job_duties_kk,
      trial_period: formValues.trial_period,
      trial_duration_months: formValues.trial_period ? formValues.trial_duration_months : undefined,
      work_start_time: formValues.work_start_time,
      work_end_time: formValues.work_end_time,
      break_start_time: formValues.has_break ? formValues.break_start_time : undefined,
      break_end_time: formValues.has_break ? formValues.break_end_time : undefined,
      working_days_list: formValues.working_days_list,
      salary_amount: formValues.salary_amount,
      employee_address_ru: formValues.employee_address_ru,
      employee_address_kk: formValues.employee_address_kk,
      work_format: formValues.work_format,
      is_parallel_worker: formValues.is_parallel_worker,
      contract_number: formValues.contract_number?.trim() || undefined,
    };
  }

  async function handlePreview() {
    const isValid = await methods.trigger();

    if (!isValid) {
      const errors = methods.formState.errors;
      const bad: SectionId[] = [];

      for (const s of SECTIONS) {
        if (
          SECTION_FIELDS[s.id].some((f) => {
            const fieldName = f as keyof ContractFormValues;
            return errors[fieldName];
          })
        ) {
          bad.push(s.id);
        }
      }

      if (errors.contract_number && !bad.includes("basic_info")) {
        bad.push("basic_info");
      }

      setSectionsWithErrors(bad);
      setShowGlobalError(true);
      return;
    }

    setShowGlobalError(false);
    setSectionsWithErrors([]);

    const formValues = methods.getValues();
    dispatch(setContractData(formValues));

    setPreviewData(buildPreviewData(formValues));
    setIsPreviewOpen(true);
  }

  function handleContractNumberConflict(error: unknown): boolean {
    const data = (error as { data?: Record<string, unknown> })?.data;
    if (data && Array.isArray(data.contract_number)) {
      setIsPreviewOpen(false);
      methods.setError("contract_number", { message: t("validation.contract_number_taken") });
      setActive("basic_info");
      return true;
    }
    return false;
  }

  async function handleCreateContract() {
    if (!previewData || !applicationId || isCreatingContract) return;

    try {
      const payload = { ...previewData };
      if (payload.contract_number && payload.contract_number === contractNumberHintRef.current) {
        delete payload.contract_number;
      }
      const res = await createContract(payload).unwrap();
      setIsPreviewOpen(false);
      setPreviewData(null);
      setCreatedNumber("contract_number" in res ? res.contract_number : null);

      methods.reset();
      dispatch(setSectionCompleteness({ section: "basic_info", complete: false }));
      dispatch(setSectionCompleteness({ section: "position_duties", complete: false }));
      dispatch(setSectionCompleteness({ section: "work_schedule", complete: false }));

      dispatch(setReadyForSigning(true));

      setShowSuccessPrompt(true);
    } catch (error: unknown) {
      if (handleContractNumberConflict(error)) return;
      setIsPreviewOpen(false);

      const errorText = extractErrorMessage(error, "messages.contractCreateError", t);

      setErrorPrompt({
        title: t("messages.errorTitle"),
        text: errorText,
      });
    }
  }

  async function handleUpdateContract() {
    if (!previewData || !contractId || isUpdatingContract) return;

    try {
      setIsUpdatingContract(true);
      await patchContract({ id: contractId, data: formValuesToContractPatch(methods.getValues()) }).unwrap();
      setIsPreviewOpen(false);
      setPreviewData(null);
      setShowSuccessPrompt(true);
    } catch (error: unknown) {
      if (handleContractNumberConflict(error)) return;
      setIsPreviewOpen(false);
      setErrorPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(error, "messages.contractUpdateError", t),
      });
    } finally {
      setIsUpdatingContract(false);
    }
  }

  const isSubmitting = isEditMode ? isUpdatingContract : isCreatingContract;
  const pageTitle = isEditMode ? t("meta.title_edit") : t("meta.title");

  if (isEditMode ? isLoadingContract : isLoadingApplication) {
    return (
      <>
        <title>{pageTitle}</title>
        <div className="h-full flex flex-col">
          <FillContractPageSkeleton />
        </div>
      </>
    );
  }

  if (isEditMode ? (isErrorContract || !contractId || !contractDetail) : (isErrorApplication || !applicationId || !applicationDetail)) {
    return (
      <>
        <title>{pageTitle}</title>
        <div className="h-full flex flex-col">
          <FillContractHeader isEditMode={isEditMode} />
          <section className="flex-1">
            <Prompt
              variant="error"
              title={t("messages.errorTitle")}
              text={isEditMode ? t("messages.loadContractError") : t("messages.loadApplicationError")}
              buttonText={t("prompt.ok")}
              onClose={() => navigate(HR_CONTRACTS_PAGE_ROUTE)}
              namespace="FillContractPage"
            />
          </section>
        </div>
      </>
    );
  }

  return (
    <>
      <title>{pageTitle}</title>
      <FormProvider {...methods}>
        <div className="h-full flex flex-col">
          <FillContractHeader
            isEditMode={isEditMode}
            onSaveDraft={isEditMode ? handleSaveContract : handleSaveFillDraft}
            isSavingDraft={isSavingDraft}
            draftSaveStatus={draftSaveStatus}
            onDraftStatusReset={() => setDraftSaveStatus("idle")}
            disabled={isSubmitting}
          />

          {(applicationDetail?.user_creation_failed || editApplicationDetail?.user_creation_failed) && (
            <div className="mb-4">
              <Toast
                color="negative"
                isFullWidth
                closable={false}
                autoClose={false}
                text={
                  applicationDetail?.user_creation_error ||
                  editApplicationDetail?.user_creation_error ||
                  t("applications.userCreationFailed")
                }
              />
            </div>
          )}

          <section className="flex-1">
              <StepsSwitcher
                steps={SECTIONS.map((section) => ({
                  id: section.id,
                  label: t(`sections.${section.id}`),
                  icon: section.icon,
                }))}
                activeStep={active}
                onStepChange={handleChangeSection}
                scrollToTopOnChange
                completedSteps={Object.entries(completeness ?? {})
                  .filter(([_, isComplete]) => isComplete === true)
                  .map(([id]) => id)}
                errorSteps={sectionsWithErrors}
                contentClassName="flex flex-col justify-between">
                <>
                  <SectionRenderer id={active} contractNumberError={contractNumberError} contractNumberEditable={isPureDraft} />

                  <div className="mt-10 flex justify-end items-center gap-3">
                    {showGlobalError && <Toast color="negative" text={t("errors.fill_all_sections")} />}

                    {isLastSection ? (
                      <>
                        <Button variant="secondary" size="lg" type="button" onClick={handleBack}>
                          {t("buttons.back")}
                        </Button>
                        <Button
                          variant="secondary"
                          size="lg"
                          type="button"
                          onClick={() => {
                            requestConfirmation(() => {
                              navigate(HR_CONTRACTS_PAGE_ROUTE);
                            });
                          }}>
                          {t("buttons.cancel")}
                        </Button>
                        {hasPermission(PERMISSIONS.HR.CREATE_CONTRACT) && (
                          <Button
                            variant="primary"
                            size="lg"
                            type="button"
                            disabled={Object.keys(methods.formState.errors).length > 0 || isNumberTaken}
                            onClick={handlePreview}>
                            {isEditMode ? t("buttons.save_contract") : t("buttons.create_contract")}
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        {!isFirstSection && (
                          <Button variant="secondary" size="lg" type="button" onClick={handleBack}>
                            {t("buttons.back")}
                          </Button>
                        )}
                        <Button
                          variant="secondary"
                          size="lg"
                          type="button"
                          onClick={() => {
                            requestConfirmation(() => {
                              navigate(HR_CONTRACTS_PAGE_ROUTE);
                            });
                          }}>
                          {t("buttons.cancel")}
                        </Button>
                        <Button variant="primary" size="lg" type="button" onClick={handleNext}>
                          {t("buttons.next")}
                        </Button>
                      </>
                    )}
                  </div>
                </>
              </StepsSwitcher>
          </section>

          {isPreviewOpen && previewData && (
            <Suspense fallback={null}>
              <ContractPreviewModal
                data={previewData}
                onClose={() => {
                  setIsPreviewOpen(false);
                  setPreviewData(null);
                }}
                onConfirm={isEditMode ? handleUpdateContract : handleCreateContract}
                isLoading={isSubmitting}
                confirmLabel={isEditMode ? t("buttons.update_contract") : undefined}
                employee_full_name={
                  isEditMode
                    ? contractDetail?.candidate_application?.full_name
                    : applicationDetail?.personal_info
                      ? `${applicationDetail.personal_info.surname || ""} ${applicationDetail.personal_info.name || ""} ${applicationDetail.personal_info.father_name || ""}`.trim()
                      : undefined
                }
                employee_iin={isEditMode ? editApplicationDetail?.personal_info?.iin : applicationDetail?.personal_info?.iin}
                employee_id_number={
                  isEditMode
                    ? editApplicationDetail?.id_documents?.national_id_number
                    : applicationDetail?.id_documents?.national_id_number
                }
                employee_id_issued_by={
                  isEditMode
                    ? editApplicationDetail?.id_documents?.national_id_issued_by
                    : applicationDetail?.id_documents?.national_id_issued_by
                }
                employee_id_issue_date={
                  isEditMode
                    ? editApplicationDetail?.id_documents?.national_id_issue_date
                    : applicationDetail?.id_documents?.national_id_issue_date
                }
                employee_phone={
                  isEditMode ? editApplicationDetail?.contact_info?.phone : applicationDetail?.contact_info?.phone
                }
              />
            </Suspense>
          )}

          {showSuccessPrompt && (
            <Prompt
              variant="success"
              title={isEditMode ? t("messages.contractUpdatedTitle") : t("messages.contractCreatedTitle")}
              text={
                isEditMode
                  ? t("messages.contractUpdatedText")
                  : t("messages.contractCreatedText", { number: createdNumber ?? "" })
              }
              buttonText={t("buttons.return_to_list")}
              onClose={() => {
                setShowSuccessPrompt(false);
                if (!isEditMode) {
                  dispatch(setReadyForSigning(false));
                }
                navigate(HR_CONTRACTS_PAGE_ROUTE);
              }}
              namespace="FillContractPage"
            />
          )}

          {errorPrompt && (
            <Prompt
              variant="error"
              title={errorPrompt.title}
              text={errorPrompt.text}
              buttonText={t("prompt.close")}
              onClose={() => setErrorPrompt(null)}
              namespace="FillContractPage"
            />
          )}

          {modalOpen && (
            <PromptForm
              variant="error"
              title={t("leave_modal.title")}
              text={t("leave_modal.description")}
              confirmText={t("leave_modal.leave")}
              cancelText={t("leave_modal.stay")}
              onConfirm={confirmNavigation}
              onClose={cancelNavigation}
              namespace="FillContractPage"
            />
          )}
        </div>
      </FormProvider>
    </>
  );
}
